<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

const { accountId } = useAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.props?.title || 'Statut des canaux');

const inboxes = computed(() => {
  if (data.value?.mapped?.inboxes && Array.isArray(data.value.mapped.inboxes)) {
    return data.value.mapped.inboxes;
  }
  if (data.value?.raw?.payload && Array.isArray(data.value.raw.payload)) {
    return data.value.raw.payload;
  }
  if (Array.isArray(data.value?.raw)) {
    return data.value.raw;
  }
  return [];
});

const getInboxIcon = channelType => {
  const icons = {
    web_widget: 'i-lucide-message-square',
    api: 'i-lucide-code',
    email: 'i-lucide-mail',
    line: 'i-lucide-message-circle',
    telegram: 'i-lucide-send',
    twitter: 'i-lucide-twitter',
    facebook: 'i-lucide-facebook',
    whatsapp: 'i-lucide-message-circle',
    sms: 'i-lucide-phone',
  };
  return icons[channelType] || 'i-lucide-mailbox';
};
</script>

<template>
  <div class="h-full flex flex-col font-inter">
    <div v-if="title" class="mb-6 flex items-center justify-between px-1">
      <h3 class="text-[13px] font-bold text-slate-900 tracking-tight">
        {{ title }}
      </h3>
      <div class="h-1.5 w-1.5 rounded-full bg-slate-950" />
    </div>
    <div class="flex-1 overflow-auto custom-scrollbar pr-1">
      <div
        v-if="loading && inboxes.length === 0"
        class="flex items-center justify-center py-16"
      >
        <div class="flex flex-col items-center gap-4">
          <div
            class="h-10 w-10 border-2 border-slate-100 border-t-slate-950 rounded-full animate-spin"
          />
          <span
            class="text-[11px] font-bold text-slate-400 uppercase tracking-widest"
            >Loading...</span>
        </div>
      </div>
      <div
        v-else-if="error && inboxes.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <div
          class="h-14 w-14 rounded-xl bg-red-50 flex items-center justify-center mb-4 border border-red-100"
        >
          <span class="i-lucide-alert-circle h-7 w-7 text-red-500" />
        </div>
        <span class="text-[13px] font-medium text-slate-500 text-center px-6">{{
          error
        }}</span>
      </div>
      <div v-else class="space-y-2 pb-4">
        <div
          v-for="(inbox, index) in inboxes"
          :key="inbox.id || index"
          class="group flex items-center justify-between p-4 rounded-xl border border-slate-100 transition-all duration-200 hover:border-slate-200 hover:bg-slate-50/50 cursor-pointer"
        >
          <div class="flex items-center gap-4 min-w-0">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-white text-slate-400 group-hover:text-slate-900 shadow-sm transition-all duration-200"
            >
              <span
                :class="getInboxIcon(inbox.channel_type || inbox.channel?.type)"
                class="text-xl"
              />
            </div>
            <div class="min-w-0">
              <p
                class="text-[13px] font-bold text-slate-900 truncate leading-tight"
              >
                {{ inbox.name }}
              </p>
              <p
                v-if="inbox.channel_type"
                class="text-[10px] text-slate-400 font-medium uppercase tracking-widest mt-0.5"
              >
                {{ inbox.channel_type }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-100 bg-white shadow-sm"
          >
            <div
              class="h-1.5 w-1.5 rounded-full"
              :class="{
                'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]':
                  inbox.status === 'connected',
                'bg-rose-500': inbox.status === 'disconnected',
                'bg-amber-500': inbox.status === 'pending',
              }"
            />
            <span
              class="text-[10px] font-bold text-slate-600 uppercase tracking-widest"
              >{{ inbox.status }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
